import { useState, useEffect } from "react";
import { LayoutDashboard, Package, Box, CheckCircle2, TrendingUp, TrendingDown } from "lucide-react";
import { readMonth, currentMonth, grandTotal } from "../services/inventory";
import type { MonthData, InventoryRow } from "../types";

interface SectionSummary {
  title: string;
  icon: typeof Package;
  color: string;
  rows: InventoryRow[];
  total: number;
  count: number;
}

export default function Dashboard() {
  const month = currentMonth();
  const [data, setData] = useState<MonthData | null>(null);

  useEffect(() => {
    readMonth(month).then(setData);
  }, [month]);

  const sections: SectionSummary[] = [
    { title: "Raw Materials", icon: Package, color: "text-blue-500", rows: data?.raw_materials ?? [], total: 0, count: 0 },
    { title: "Packaging", icon: Box, color: "text-amber-500", rows: data?.packaging ?? [], total: 0, count: 0 },
    { title: "Finished Goods", icon: CheckCircle2, color: "text-green-500", rows: data?.finished_goods ?? [], total: 0, count: 0 },
  ];

  for (const s of sections) {
    s.total = grandTotal(s.rows);
    s.count = s.rows.length;
  }

  const grand = sections.reduce((sum, s) => sum + s.total, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-citilux-blue-600/20 flex items-center justify-center">
          <LayoutDashboard className="w-5 h-5 text-citilux-blue-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-metallic-silver/60">{month}</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {sections.map((s) => (
          <div key={s.title} className="bg-white dark:bg-navy-800/40 border border-light-border dark:border-navy-700/40 rounded-2xl p-5 space-y-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl bg-current/10 flex items-center justify-center ${s.color}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-metallic-silver">{s.title}</p>
                <p className="text-xs text-gray-400 dark:text-metallic-silver/50">{s.count} item{s.count !== 1 ? "s" : ""}</p>
              </div>
            </div>
            <p className={`text-2xl font-bold tabular-nums ${s.total >= 0 ? "text-gray-900 dark:text-white" : "text-red-500"}`}>
              ₦{s.total.toLocaleString("en-US", { minimumFractionDigits: 2 })}
            </p>
          </div>
        ))}
      </div>

      {/* Grand total */}
      <div className="bg-gradient-to-br from-citilux-blue-600 to-citilux-blue-800 rounded-2xl p-6 text-white">
        <p className="text-sm font-medium text-white/70 uppercase tracking-wider">Grand Total</p>
        <p className="text-3xl sm:text-4xl font-bold tabular-nums mt-1">
          ₦{grand.toLocaleString("en-US", { minimumFractionDigits: 2 })}
        </p>
        <p className="text-xs text-white/50 mt-2">{month} ledger</p>
      </div>

      {/* Recent activity */}
      <div className="bg-white dark:bg-navy-800/40 border border-light-border dark:border-navy-700/40 rounded-2xl p-5">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-white/80 mb-3">Recent Activity</h3>
        {sections.every((s) => s.count === 0) ? (
          <p className="text-sm text-gray-400 dark:text-metallic-silver/50 text-center py-8">
            No entries for {month}. Add items in any section to see activity here.
          </p>
        ) : (
          <div className="space-y-2">
            {sections.map((s) => {
              const latest = s.rows[s.rows.length - 1];
              if (!latest) return null;
              return (
                <div key={s.title} className="flex items-center gap-3 text-sm">
                  <div className={`w-2 h-2 rounded-full ${s.color} bg-current`} />
                  <span className="text-gray-500 dark:text-metallic-silver/70 min-w-[110px]">{s.title}</span>
                  <span className="text-gray-700 dark:text-white/80 font-medium truncate">{latest.item}</span>
                  <span className="ml-auto text-gray-400 dark:text-metallic-silver/60 tabular-nums">
                    {latest.quantity > 0 ? <TrendingUp className="w-3.5 h-3.5 inline text-green-500" /> : <TrendingDown className="w-3.5 h-3.5 inline text-red-500" />}
                    {" "}{Math.abs(latest.quantity)} {latest.unit}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
